import React, { useState, useRef } from 'react';
import { Briefcase, Upload, CheckCircle2, AlertCircle, FileText, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = ['Cameriere', 'Pizzaiolo', 'Cuoco', 'Aiuto Cucina', 'Lavapiatti', 'Altro'];
const MAX_BYTES = 5 * 1024 * 1024;

const LavoraConNoi = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: 'Cameriere',
    message: '',
  });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text }
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setFeedback({ type: 'error', text: 'Il CV deve essere un file PDF.' });
      e.target.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      setFeedback({ type: 'error', text: 'Il file supera i 5 MB.' });
      e.target.value = '';
      return;
    }
    setFeedback(null);
    setCv(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cv) {
      setFeedback({ type: 'error', text: 'Carica il tuo CV in PDF.' });
      return;
    }
    setSubmitting(true);
    setFeedback(null);

    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append('cv', cv);
      const res = await fetch(`${API}/careers/apply`, { method: 'POST', body: data });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Errore invio candidatura');
      setFeedback({
        type: 'success',
        text: 'Candidatura inviata! Ti contatteremo presto. Grazie.',
      });
      setForm({ name: '', email: '', phone: '', position: 'Cameriere', message: '' });
      setCv(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lavora"
      data-testid="careers-section"
      className="py-24 bg-gradient-to-br from-stone-50 to-amber-50"
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-6 py-2 bg-[#7f9e60]/10 rounded-full mb-4">
              <Briefcase className="w-5 h-5 text-[#7f9e60]" />
              <span className="text-[#7f9e60] font-semibold uppercase tracking-wide text-sm">
                Unisciti al Team
              </span>
            </div>
            <h2
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Lavora con Noi
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Sei appassionato di cucina e ospitalità? Mandaci il tuo CV: cerchiamo sempre
              talenti che vogliono crescere con la famiglia Grano e Sale.
            </p>
            <div className="w-24 h-1 bg-[#7f9e60] mx-auto rounded-full mt-6"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left side info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3
                  className="text-2xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  Cosa Cerchiamo
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#7f9e60] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Passione vera per il cibo e l&apos;ospitalità</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#7f9e60] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Voglia di imparare e crescere nel team</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#7f9e60] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Spirito di squadra e affidabilità</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#7f9e60] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Esperienza è un plus, ma non obbligatoria</span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#7f9e60] rounded-2xl p-8 text-white shadow-lg">
                <h3
                  className="text-2xl font-bold mb-3"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  Le Tue Posizioni
                </h3>
                <div className="flex flex-wrap gap-2">
                  {POSITIONS.map((p) => (
                    <span
                      key={p}
                      className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right side form */}
            <form
              onSubmit={handleSubmit}
              className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-xl space-y-5"
              data-testid="careers-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome e Cognome *
                  </label>
                  <input
                    required
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="careers-name-input"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition"
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="careers-email-input"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition"
                    placeholder="mario@email.it"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefono *
                  </label>
                  <input
                    required
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="careers-phone-input"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition"
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Posizione *
                  </label>
                  <select
                    name="position"
                    value={form.position}
                    onChange={handleChange}
                    data-testid="careers-position-select"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition bg-white"
                  >
                    {POSITIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Messaggio (facoltativo)
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={1000}
                  data-testid="careers-message-textarea"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition resize-none"
                  placeholder="Raccontaci qualcosa di te, la tua esperienza, le tue passioni..."
                />
              </div>

              {/* CV upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Carica il tuo CV (PDF, max 5MB) *
                </label>
                <label
                  htmlFor="cv-file"
                  className="cursor-pointer flex items-center justify-between gap-4 px-5 py-4 rounded-lg border-2 border-dashed border-[#7f9e60]/50 bg-[#7f9e60]/5 hover:bg-[#7f9e60]/10 transition"
                  data-testid="careers-cv-dropzone"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {cv ? (
                      <>
                        <FileText className="w-6 h-6 text-[#7f9e60] flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate" data-testid="cv-filename">
                            {cv.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(cv.size / 1024).toFixed(0)} KB
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-[#7f9e60]" />
                        <span className="text-gray-700">
                          Clicca per selezionare il PDF del tuo CV
                        </span>
                      </>
                    )}
                  </div>
                  <span className="px-4 py-2 bg-[#7f9e60] text-white rounded-full text-sm font-semibold flex-shrink-0">
                    {cv ? 'Cambia file' : 'Scegli file'}
                  </span>
                </label>
                <input
                  id="cv-file"
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFile}
                  data-testid="careers-cv-input"
                  className="hidden"
                />
              </div>

              {/* Feedback */}
              {feedback && (
                <div
                  data-testid="careers-feedback"
                  className={`flex items-start gap-3 p-4 rounded-lg ${
                    feedback.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {feedback.type === 'success' ? (
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  )}
                  <p className="text-sm">{feedback.text}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="careers-submit-button"
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#7f9e60] text-white text-lg font-semibold rounded-full hover:bg-[#6a8550] transition-all duration-300 hover:scale-[1.02] shadow-lg disabled:opacity-60 disabled:hover:scale-100"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Briefcase className="w-5 h-5" />
                    Invia Candidatura
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Inviando la candidatura accetti il trattamento dei tuoi dati per le finalità di
                selezione del personale.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LavoraConNoi;
